import {
    defaultApplicationFilters,
    defaultShortlistedFilters,
    getApplicationFiltersFromRequirements,
} from 'Components/campaigns/applications/ApplicationFilters';
import ShortlistedSummaryPanel from 'Components/campaigns/applications/ShortlistedSummaryPanel';
import { PRIVILEGE } from 'constants/privilege';
import withAccessControl from 'hoc/withAccessControl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { fetchShortlistedSummary } from 'services/api/campaign/application';
import { RootState } from 'store';
import { ShortlistedSummary } from 'types/campaign/applications';
import CampaignApplicationsList from './CampaignApplicationsList';
import { useGetCampaignCurrency } from 'slices/campaigns/selector';

const SUMMARY_FETCH_DEBOUNCE_MS = 200;

const CampaignApplications = () => {
    const { campaignId } = useParams();
    const location = useLocation();
    const bookmarked = location.pathname.includes('/shortlisted') ? 'y' : 'n';
    const isShortlistedTab = bookmarked === 'y';
    const requirements = useSelector(
        (state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign?.requirements
    );

    const campaign = useSelector(
        (state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign
    );

    const shortlistedSummaryEpoch = useSelector(
        (state: RootState) =>
            state.campaignApplications.shortlistedSummaryEpoch[campaignId!] ?? 0
    );

    const [shortlistedSummary, setShortlistedSummary] = useState<ShortlistedSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(isShortlistedTab);
    const summaryRequestIdRef = useRef(0);

    const currency = useGetCampaignCurrency(campaignId!);

    const tabDefaultFilters = useMemo(
        () =>
            getApplicationFiltersFromRequirements(
                requirements,
                isShortlistedTab ? defaultShortlistedFilters : defaultApplicationFilters
            ),
        [requirements, isShortlistedTab]
    );

    const resetFilters = isShortlistedTab ? defaultShortlistedFilters : defaultApplicationFilters;

    const loadShortlistedSummary = useCallback(async () => {
        if (!campaignId) {
            setSummaryLoading(false);
            return;
        }

        const requestId = ++summaryRequestIdRef.current;
        setSummaryLoading(true);

        try {
            const summary = await fetchShortlistedSummary(campaignId);
            if (requestId !== summaryRequestIdRef.current) {
                return;
            }
            setShortlistedSummary(summary);
        } catch {
            if (requestId !== summaryRequestIdRef.current) {
                return;
            }
            setShortlistedSummary(null);
        } finally {
            if (requestId === summaryRequestIdRef.current) {
                setSummaryLoading(false);
            }
        }
    }, [campaignId]);

    useEffect(() => {
        if (!isShortlistedTab) {
            setShortlistedSummary(null);
            setSummaryLoading(false);
            return;
        }

        if (!campaignId) {
            setSummaryLoading(false);
            return;
        }

        const timer = window.setTimeout(() => {
            loadShortlistedSummary();
        }, SUMMARY_FETCH_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
            summaryRequestIdRef.current += 1;
        };
    }, [campaignId, isShortlistedTab, shortlistedSummaryEpoch, loadShortlistedSummary]);

    return (
        <CampaignApplicationsList
            viewBestCreative={campaign?.attach_social_post}
            bookmarked={bookmarked}
            tabDefaultFilters={tabDefaultFilters}
            resetFilters={resetFilters}
            hasTransactions={campaign?.has_transactions}
            documentTitle={isShortlistedTab ? 'Shortlisted - Campaign' : 'Applications - Campaign'}
            emptyStateMessage={
                isShortlistedTab
                    ? 'Currently there are no shortlisted applications, try adjusting filters!'
                    : 'Currently there is no application in your list, try adjusting filters!'
            }
            sidebar={
                isShortlistedTab ? (
                    <ShortlistedSummaryPanel summary={shortlistedSummary} loading={summaryLoading} currency={currency} />
                ) : undefined
            }
        />
    );
};

export default withAccessControl(CampaignApplications, PRIVILEGE.CAMPAIGN.APPLICATION.INDEX);
